import { useEffect, useRef, useState } from 'react';
import { getSurveyById } from '../../utils/surveyApi';
import {
  dynamicSectionConfigFromRow,
  effectiveRubricStatus,
  type AiDynamicSectionConfig,
} from './aiDynamicSection';

/** No urgency to the first check — save no longer blocks on derivation. See ADR-0002 §3. */
export const RUBRIC_POLL_INTERVAL_MS = 3_000;

/** Hard cap (~2 min) — past this the checklist reports it could not confirm, never spins forever. */
export const RUBRIC_POLL_MAX_ATTEMPTS = 40;

export interface UseRubricStatusPollingParams {
  surveyId: number | null;
  questionId: string;
  /** Caller decides this (typically `effectiveRubricStatus(...) === 'pending'`); the hook owns only
   *  the timer, not the status logic. */
  isPending: boolean;
  /** Fires once the row's rubric is no longer pending. Never called after the poll times out. */
  onResolved: (
    update: Pick<AiDynamicSectionConfig, 'rubric' | 'rubric_status' | 'rubric_stale'>,
  ) => void;
}

export interface UseRubricStatusPollingResult {
  /** True once the attempt cap was hit while still pending — display like a backend failure. */
  timedOut: boolean;
}

/** Polls `GET /api/surveys/:id` while `isPending`, watching one question's rubric leave `pending`.
 *  Stops on resolve, unmount, or `RUBRIC_POLL_MAX_ATTEMPTS`. See ADR-0002 §3. */
export function useRubricStatusPolling({
  surveyId,
  questionId,
  isPending,
  onResolved,
}: UseRubricStatusPollingParams): UseRubricStatusPollingResult {
  const [timedOut, setTimedOut] = useState(false);
  const onResolvedRef = useRef(onResolved);
  useEffect(() => {
    onResolvedRef.current = onResolved;
  }, [onResolved]);

  useEffect(() => {
    if (!isPending || surveyId === null) return;

    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const { survey } = await getSurveyById(surveyId);
        const row = survey.questions?.find((q) => q.id === questionId);
        if (cancelled) return;

        if (row) {
          const config = dynamicSectionConfigFromRow(row);
          if (effectiveRubricStatus(config.rubric_status) !== 'pending') {
            onResolvedRef.current({
              rubric: config.rubric,
              rubric_status: config.rubric_status,
              rubric_stale: config.rubric_stale,
            });
            return;
          }
        }
      } catch {
        // Transient fetch failure — retry next tick, still counted against the attempt cap.
      }

      if (cancelled) return;
      if (attempts >= RUBRIC_POLL_MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timerId = setTimeout(tick, RUBRIC_POLL_INTERVAL_MS);
    };

    setTimedOut(false);
    timerId = setTimeout(tick, RUBRIC_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerId !== null) clearTimeout(timerId);
    };
  }, [isPending, surveyId, questionId]);

  // Masked by `isPending` rather than reset via setState — see tick()'s own reset above.
  return { timedOut: isPending && timedOut };
}
